import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { AuthContext } from '../App';
import { toast } from 'sonner';
import KerbCoverSlabSection from './KerbCoverSlabSection';

const OTHER_VALUE = '__other__';

/**
 * Stone Details block used on Create/Edit Memorial Order forms.
 *
 *  - Memorial Type FIRST (Cremation vs Full Size) — gates the shape list.
 *  - Shape dropdown from Corel Integration → Memorial Shapes filtered by
 *    memorial_type. Shapes with no memorial_type set appear in BOTH lists
 *    so legacy data isn't hidden.
 *  - Colour dropdown from Corel Granites (REQUIRED).
 *  - "Other..." reveals a free-text input. On blur the value is POSTed to
 *    /corel/admin/<resource>/ensure which creates a stub record so the
 *    next operator sees it in the dropdown.
 *  - Stone Code: optional free text.
 *
 * Controlled — parent owns formData / setFormData.
 */
export default function StoneDetailsSection({ formData, setFormData }) {
  const { token, API } = useContext(AuthContext);
  const [shapes, setShapes] = useState([]);
  const [granites, setGranites] = useState([]);
  // Explicit "Other" mode flags — these flip to true the moment the user
  // selects the Other option in the dropdown and stay true until they pick
  // a real catalog entry. Derived-from-value alone wasn't enough because the
  // value is empty before the user types anything, which used to make the
  // free-text input vanish (P0 UX bug caught by testing agent).
  const [shapeOtherMode, setShapeOtherMode] = useState(false);
  const [colourOtherMode, setColourOtherMode] = useState(false);

  const fetchShapes = async () => {
    try {
      const res = await axios.get(`${API}/corel/admin/shapes?active_only=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShapes(res.data || []);
    } catch (e) {
      setShapes([]);
    }
  };
  const fetchGranites = async () => {
    try {
      const res = await axios.get(`${API}/corel/admin/granites?active_only=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGranites(res.data || []);
    } catch (e) {
      setGranites([]);
    }
  };

  // Initial catalog fetch. Inline to satisfy react-hooks/set-state-in-effect.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [sRes, gRes] = await Promise.all([
          axios.get(`${API}/corel/admin/shapes?active_only=true`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/corel/admin/granites?active_only=true`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        if (cancelled) return;
        setShapes(sRes.data || []);
        setGranites(gRes.data || []);
      } catch (e) {
        if (cancelled) return;
        setShapes([]);
        setGranites([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Filter shapes by selected Memorial Type. Robust to legacy data:
  //   - case-insensitive match ("Cremation"/"cremation" both work)
  //   - common legacy synonyms map to the new canonical types
  //     (Headstone / Kerb Set / Plaque / Vase → Full Size Memorial,
  //      Cremation → Cremation Memorial)
  //   - shapes with no memorial_type set appear in BOTH lists so existing
  //     un-tagged catalog data is never hidden.
  const matchesMemorialType = (shape) => {
    if (!formData.memorial_type) return true;
    const raw = (shape.memorial_type || '').trim().toLowerCase();
    if (!raw) return true;
    if (raw === formData.memorial_type) return true;
    if (formData.memorial_type === 'cremation') {
      return raw === 'cremation' || raw === 'cremation memorial';
    }
    if (formData.memorial_type === 'full') {
      return ['full', 'full size memorial', 'full memorial',
              'headstone', 'kerb set', 'kerb', 'plaque', 'vase', 'other'].includes(raw);
    }
    return false;
  };
  const filteredShapes = shapes.filter(matchesMemorialType);

  // "Other" is active if explicitly selected via dropdown OR the saved value
  // isn't found in the catalog (covers edit-load and pasted-in legacy data).
  const shapeNotInCatalog = !!(formData.stone_shape && shapes.length > 0 && !shapes.some(s => s.name === formData.stone_shape));
  const colourNotInCatalog = !!(formData.stone_colour && granites.length > 0 && !granites.some(g => g.name === formData.stone_colour));
  const shapeIsOther = shapeOtherMode || shapeNotInCatalog;
  const colourIsOther = colourOtherMode || colourNotInCatalog;

  const ensureCustomShape = async (name) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return;
    try {
      await axios.post(`${API}/corel/admin/shapes/ensure`, {
        name: trimmed,
        memorial_type: formData.memorial_type || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Saved "${trimmed}" to Corel shape catalog. Add vector & thumbnail later from Corel Integration.`);
      fetchShapes();
    } catch (e) {
      console.warn('Failed to ensure shape in Corel', e);
    }
  };
  const ensureCustomColour = async (name) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return;
    try {
      await axios.post(`${API}/corel/admin/granites/ensure`, {
        name: trimmed
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Saved "${trimmed}" to Corel granite catalog. Add texture later from Corel Integration.`);
      fetchGranites();
    } catch (e) {
      console.warn('Failed to ensure granite in Corel', e);
    }
  };

  return (
    <div className="rounded-lg border-l-4 border-slate-500 bg-slate-50 p-5 space-y-4">
      <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-slate-500"></span>
        Stone Details
      </h3>

      {/* Memorial Type — first, drives the shape filter */}
      <div className="space-y-2">
        <Label className="text-slate-700">Memorial Type *</Label>
        <div className="flex flex-wrap gap-2" data-testid="memorial-type-group">
          {[
            { value: 'cremation', label: 'Cremation Memorial' },
            { value: 'full', label: 'Full Size Memorial' }
          ].map(opt => {
            const active = formData.memorial_type === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setFormData({ ...formData, memorial_type: opt.value })}
                data-testid={`memorial-type-${opt.value}`}
                className={`px-4 py-2 rounded-md border text-sm font-medium transition ${
                  active
                    ? 'bg-slate-800 text-white border-slate-800'
                    : 'bg-white text-slate-700 border-slate-300 hover:border-slate-500'
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        {!formData.memorial_type && (
          <p className="text-xs text-amber-700">Select a type first — the Stone Shape list will filter to match.</p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Stone Shape */}
        <div className="space-y-2">
          <Label htmlFor="stone_shape" className="text-slate-700">Stone Shape</Label>
          <select
            id="stone_shape"
            value={shapeIsOther ? OTHER_VALUE : (formData.stone_shape || '')}
            onChange={(e) => {
              const v = e.target.value;
              if (v === OTHER_VALUE) {
                // Switch to free-text mode; preserve any prior typed value
                setShapeOtherMode(true);
                setFormData({ ...formData, stone_shape: formData.stone_shape || '' });
              } else {
                setShapeOtherMode(false);
                setFormData({ ...formData, stone_shape: v });
              }
            }}
            className="w-full h-10 px-3 text-sm border border-slate-300 rounded-md bg-white"
            data-testid="stone-shape-select"
          >
            <option value="">-- Select shape --</option>
            {filteredShapes.map(s => (
              <option key={s.id} value={s.name}>
                {s.name}{s.category ? ` (${s.category})` : ''}
              </option>
            ))}
            <option value={OTHER_VALUE}>Other (type below)…</option>
          </select>
          {shapeIsOther && (
            <Input
              value={formData.stone_shape || ''}
              onChange={(e) => setFormData({ ...formData, stone_shape: e.target.value })}
              onBlur={() => ensureCustomShape(formData.stone_shape)}
              placeholder="Type custom shape name…"
              data-testid="stone-shape-custom-input"
            />
          )}
        </div>

        {/* Stone Type — free text (Granite / Marble / Slate / etc.) */}
        <div className="space-y-2">
          <Label htmlFor="stone_type" className="text-slate-700">Stone Type</Label>
          <Input
            id="stone_type"
            value={formData.stone_type}
            onChange={(e) => setFormData({ ...formData, stone_type: e.target.value })}
            placeholder="e.g. Granite, Marble, Slate"
            data-testid="stone-type-input"
          />
        </div>
      </div>

      {/* Stone Colour — REQUIRED */}
      <div className="space-y-2">
        <Label htmlFor="stone_colour" className="text-slate-700">Stone Colour *</Label>
        <select
          id="stone_colour"
          value={colourIsOther ? OTHER_VALUE : (formData.stone_colour || '')}
          onChange={(e) => {
            const v = e.target.value;
            if (v === OTHER_VALUE) {
              setColourOtherMode(true);
              setFormData({ ...formData, stone_colour: formData.stone_colour || '' });
            } else {
              setColourOtherMode(false);
              setFormData({ ...formData, stone_colour: v });
            }
          }}
          required
          className="w-full h-10 px-3 text-sm border border-slate-300 rounded-md bg-white"
          data-testid="stone-colour-select"
        >
          <option value="">-- Select colour --</option>
          {granites.map(g => (
            <option key={g.id} value={g.name}>
              {g.name}{g.supplier ? ` — ${g.supplier}` : ''}
            </option>
          ))}
          <option value={OTHER_VALUE}>Other (type below)…</option>
        </select>
        {colourIsOther && (
          <Input
            value={formData.stone_colour || ''}
            onChange={(e) => setFormData({ ...formData, stone_colour: e.target.value })}
            onBlur={() => ensureCustomColour(formData.stone_colour)}
            placeholder="Type custom colour name…"
            data-testid="stone-colour-custom-input"
            required
          />
        )}
      </div>

      {/* Stone Code — optional free text */}
      <div className="space-y-2">
        <Label htmlFor="stone_code" className="text-slate-700">Stone Code <span className="text-xs text-slate-500">(optional)</span></Label>
        <Input
          id="stone_code"
          value={formData.stone_code}
          onChange={(e) => setFormData({ ...formData, stone_code: e.target.value })}
          placeholder="e.g. GG-001 (optional)"
          data-testid="stone-code-input"
        />
      </div>

      {/* Kerb Surround & Cover Slab */}
      <KerbCoverSlabSection formData={formData} setFormData={setFormData} />
    </div>
  );
}
